import { useState } from "react";
import { Plus, Minus, HelpCircle, Phone, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";

/**
 * ⚠️ 10Web AI WARNING — DO NOT REPLACE <Icon /> JSX with <img> tags.
 * All lucide icons here are wrapped with data-icon-container="lucide-do-not-replace".
 * Keep them as React components.
 */

const NAVY = "#081438";
const NAVY_DEEP = "#050D24";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const FAQS = [{
  q: "How often should I service my garage door in Sydney?",
  a: "Most Sydney households should book an annual service — once every 12 months. Coastal homes (within 1 km of the beach) and busy households (6+ opens per day) do better on a bi-annual cadence — autumn and spring. New doors should be given a first service after the first year to catch any settling and tighten everything that's bedded in."
}, {
  q: "How long does an annual service actually take?",
  a: "Plan for about an hour on-site. That includes the full 12-point inspection, lubrication, spring tension re-balance, safety reverse test, and a stamped photo report. We book in 1-hour windows — never a vague 4-hour 'sometime today' fog."
}, {
  q: "What's actually included in the 12-point inspection?",
  a: "Springs &amp; tension (3 checks), tracks &amp; rollers (3 checks), opener &amp; safety systems (3 checks), and comfort &amp; finish (3 checks: remote function, weather seals, full lubrication). Each item is photographed, marked pass / flag / fix-now on a written report you keep. AS/NZS 4505 safety reverse test included on every visit."
}, {
  q: "Do you service all garage door brands?",
  a: "Yes — B&amp;D, Merlin, Chamberlain, ATA, Steel-Line, LiftMaster, Marantec, Gliderol, Centsys, Centurion, Boss, Tilta-Door, Stratco, and any older legacy door. If we can't service it, we'll tell you on the phone before we book the visit."
}, {
  q: "Do I need to be home during the service?",
  a: "Not strictly — but we recommend it for the first visit. We like to walk the door with you, hear about any noises or quirks, and explain the report at the end. After we know your door, plan customers often leave us with garage access and find the stamped report waiting at the end of the visit."
}, {
  q: "How do I know if my door even needs servicing?",
  a: "If you can answer 'yes' to any of these, book one: door is noisier than 12 months ago, opener works harder, springs look stretched or coiled tight, cables have visible fraying, or it's been more than 12 months since the last visit. Doors don't fail dramatically — they wear quietly, then snap."
}, {
  q: "What if you find something that needs urgent repair?",
  a: "We'll show you the part, photograph it, and quote you flat-price right there. Nothing fixed without your sign-off. If the door is unsafe to use, we'll be honest about that too. Plan customers get 10% off any urgent fix found during the service."
}, {
  q: "Is an annual service really worth the cost?",
  a: "Compared to an emergency callout? Roughly five times less. Compared to a snapped spring that drops the door onto your car? No comparison. Beyond cost — a serviced door is quieter, smoother, lasts ~15+ years instead of ~7, and gives you a stamped record that helps with insurance, strata, and resale."
}];
export default function ServiceFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const schemaJson = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map(f => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a.replace(/&amp;/g, "&")
      }
    }))
  };
  return <section className="relative overflow-hidden" style={{
    backgroundColor: NAVY,
    fontFamily: FONT_BODY,
    paddingTop: "110px",
    paddingBottom: "110px"
  }}>
      {/* SEO: FAQ Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(schemaJson)
    }} />

      {/* Ambient radial */}
      <div aria-hidden className="absolute inset-0" style={{
      background: `radial-gradient(circle at 10% 0%, ${NAVY_LIGHT}55 0%, ${NAVY} 50%), radial-gradient(circle at 90% 100%, ${GOLD}10 0%, transparent 40%)`
    }} />

      {/* Grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")"
    }} />

      {/* Top gold scanline */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD}66 50%, transparent)`
    }} />

      {/* Watermark */}
      <div aria-hidden className="absolute pointer-events-none select-none" style={{
      right: "-30px",
      top: "40px",
      fontFamily: FONT_HEAD,
      fontSize: "240px",
      fontWeight: 800,
      lineHeight: 1,
      letterSpacing: "-0.05em",
      color: `${GOLD}12`
    }}>
        09
      </div>

      <div className="relative max-w-[1180px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-12 lg:gap-16 items-start">
          {/* LEFT — sticky header */}
          <div className="lg:sticky lg:top-28">
            <div className="mb-5 flex items-center gap-3">
              <div aria-hidden className="h-px w-12" style={{
              backgroundColor: GOLD
            }} />
              <span style={{
              fontFamily: FONT_HEAD,
              fontSize: "12px",
              fontWeight: 700,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: GOLD
            }}>
                09 · Service &amp; Maintenance FAQ
              </span>
            </div>

            <h2 style={{
            fontFamily: FONT_HEAD,
            fontWeight: 800,
            fontSize: "clamp(2.1rem, 4vw, 3.4rem)",
            lineHeight: 1.05,
            letterSpacing: "-0.025em",
            color: "#ffffff",
            marginBottom: "20px"
          }}>
              The questions before
              <br />
              <span style={{
              color: GOLD
            }}>booking the first visit</span>.
            </h2>

            <p style={{
            fontFamily: FONT_BODY,
            fontSize: "16px",
            lineHeight: 1.7,
            color: "rgba(255,255,255,0.72)",
            maxWidth: "420px",
            marginBottom: "32px"
          }}>
              Eight straight answers — pulled from real conversations with
              Sydney homeowners. Don't see yours? Call and ask.
            </p>

            {/* Help card */}
            <div className="rounded-2xl p-6 relative overflow-hidden" style={{
            background: `linear-gradient(135deg, ${NAVY_LIGHT}AA 0%, ${NAVY_DEEP} 100%)`,
            border: `1px solid ${GOLD}33`
          }}>
              <div className="flex items-start gap-4 mb-5">
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-lg flex-shrink-0" style={{
                width: 44,
                height: 44,
                backgroundColor: `${GOLD}22`,
                border: `1px solid ${GOLD}44`
              }}>
                  <HelpCircle size={20} color={GOLD} strokeWidth={2.5} />
                </span>
                <div>
                  <h3 style={{
                  fontFamily: FONT_HEAD,
                  fontWeight: 800,
                  fontSize: "1.05rem",
                  color: "#ffffff",
                  letterSpacing: "-0.01em",
                  marginBottom: "4px"
                }}>
                    Still unsure?
                  </h3>
                  <p style={{
                  fontFamily: FONT_BODY,
                  fontSize: "13px",
                  color: "rgba(255,255,255,0.7)",
                  lineHeight: 1.55
                }}>
                    Send a quick voice note on WhatsApp describing the noise
                    or symptom — we'll tell you if it's a service or repair
                    call.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2.5">
                <a href="tel:0485000933" className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full transition-all hover:scale-[1.02]" style={{
                backgroundColor: GOLD,
                color: NAVY,
                fontFamily: FONT_HEAD,
                fontWeight: 700,
                fontSize: "13px",
                boxShadow: `0 6px 18px ${GOLD}33`
              }}>
                  <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                    <Phone size={14} strokeWidth={2.5} />
                  </span>
                  Call 0485 000 933
                </a>

                <a href="https://wa.me/61485000933?text=Hi%2C%20I%27d%20like%20advice%20on%20whether%20my%20garage%20door%20needs%20a%20service." target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full transition-all hover:scale-[1.02]" style={{
                backgroundColor: "#25D366",
                color: "#081438",
                fontFamily: FONT_HEAD,
                fontWeight: 700,
                fontSize: "13px"
              }}>
                  WhatsApp a voice note
                  <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                    <ArrowRight size={13} strokeWidth={2.5} />
                  </span>
                </a>
              </div>
            </div>
          </div>

          {/* RIGHT — accordion */}
          <div>
            <ul className="space-y-3">
              {FAQS.map((faq, i) => {
              const isOpen = openIndex === i;
              return <li key={faq.q} className="rounded-xl overflow-hidden transition-all" style={{
                backgroundColor: isOpen ? `${NAVY_LIGHT}88` : `${NAVY_LIGHT}33`,
                border: `1px solid ${isOpen ? `${GOLD}44` : "rgba(255,255,255,0.08)"}`
              }}>
                    <button type="button" onClick={() => setOpenIndex(isOpen ? null : i)} className="w-full text-left flex items-start justify-between gap-4 p-5 md:p-6 transition-colors" aria-expanded={isOpen}>
                      <div className="flex items-start gap-4 flex-1">
                        {/* Q number */}
                        <span className="flex-shrink-0" style={{
                      fontFamily: FONT_HEAD,
                      fontSize: "13px",
                      fontWeight: 800,
                      color: GOLD,
                      letterSpacing: "0.05em",
                      marginTop: "2px",
                      minWidth: "28px"
                    }}>
                          Q{String(i + 1).padStart(2, "0")}
                        </span>

                        <h3 style={{
                      fontFamily: FONT_HEAD,
                      fontWeight: 700,
                      fontSize: "15.5px",
                      lineHeight: 1.35,
                      color: "#ffffff",
                      letterSpacing: "-0.01em",
                      flex: 1
                    }} dangerouslySetInnerHTML={{
                      __html: faq.q
                    }} />
                      </div>

                      {/* Toggle icon */}
                      <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-full flex-shrink-0 mt-0.5" style={{
                    width: 28,
                    height: 28,
                    backgroundColor: isOpen ? GOLD : "rgba(245,166,35,0.15)",
                    border: `1px solid ${isOpen ? GOLD : `${GOLD}44`}`,
                    transition: "all 0.2s ease"
                  }}>
                        {isOpen ? <Minus size={14} color={NAVY} strokeWidth={3} /> : <Plus size={14} color={GOLD} strokeWidth={3} />}
                      </span>
                    </button>

                    {/* Answer */}
                    {isOpen && <div className="px-5 md:px-6 pb-6" style={{
                  paddingLeft: "70px"
                }}>
                        <div className="pt-1 border-t" style={{
                    borderColor: "rgba(255,255,255,0.08)"
                  }}>
                          <p style={{
                      fontFamily: FONT_BODY,
                      fontSize: "14.5px",
                      lineHeight: 1.7,
                      color: "rgba(255,255,255,0.78)",
                      paddingTop: "16px"
                    }} dangerouslySetInnerHTML={{
                      __html: faq.a
                    }} />
                        </div>
                      </div>}
                  </li>;
            })}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom gold scanline */}
      <div aria-hidden className="absolute bottom-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD}66 50%, transparent)`
    }} />
    </section>;
}